import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, ChevronRight, Loader2, ShieldCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "../hooks/use-toast";
import { createCheckoutSession } from "../lib/api";
import { trackInitiateCheckout } from "../lib/tracking";
import { useAuth } from "../context/AuthContext";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    email: "", firstName: "", lastName: "", address: "", city: "", zip: "", phone: "",
  });

  useEffect(() => {
    if (user) {
      setForm((f) => ({
        ...f,
        email: f.email || user.email || "",
        firstName: f.firstName || (user.name ? user.name.split(" ")[0] : ""),
        lastName: f.lastName || (user.name ? user.name.split(" ").slice(1).join(" ") : ""),
      }));
    }
  }, [user]);

  const shipping = subtotal >= 35 || subtotal === 0 ? 0 : 4.99;
  const total = subtotal + shipping;

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      trackInitiateCheckout(items, total);
      const payload = {
        items: items.map((i) => ({ id: i.id, qty: i.qty })),
        customer: { ...form },
        origin_url: window.location.origin,
      };
      const res = await createCheckoutSession(payload);
      // Redirect to Stripe hosted checkout
      window.location.href = res.checkout_url;
    } catch (err) {
      toast({ title: "Erro ao iniciar pagamento", description: "Tenta novamente.", variant: "destructive" });
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <h1 className="font-serif text-3xl mb-4">O teu carrinho está vazio</h1>
        <p className="text-muted-foreground mb-8">Adiciona produtos antes de finalizar a compra.</p>
        <Link to="/loja">
          <Button className="rounded-full h-12 px-8">Explorar loja</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <nav className="text-sm text-muted-foreground mb-6 flex items-center gap-1">
        <Link to="/" className="hover:text-primary">Início</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-foreground">Checkout</span>
      </nav>

      <h1 className="font-serif text-3xl md:text-4xl mb-8">Finalizar compra</h1>

      <form onSubmit={handleSubmit} className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-8">
          <section>
            <h2 className="text-lg font-medium mb-4">Contacto</h2>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={form.email} onChange={setField("email")} placeholder="o-teu@email.com" className="mt-1.5 h-11" />
            </div>
          </section>

          <section>
            <h2 className="text-lg font-medium mb-4">Morada de entrega</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="fname">Nome</Label>
                <Input id="fname" required value={form.firstName} onChange={setField("firstName")} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="lname">Apelido</Label>
                <Input id="lname" required value={form.lastName} onChange={setField("lastName")} className="mt-1.5 h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="address">Morada</Label>
                <Input id="address" required value={form.address} onChange={setField("address")} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="city">Cidade</Label>
                <Input id="city" required value={form.city} onChange={setField("city")} className="mt-1.5 h-11" />
              </div>
              <div>
                <Label htmlFor="zip">Código postal</Label>
                <Input id="zip" required value={form.zip} onChange={setField("zip")} placeholder="0000-000" className="mt-1.5 h-11" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="phone">Telemóvel</Label>
                <Input id="phone" type="tel" required value={form.phone} onChange={setField("phone")} className="mt-1.5 h-11" />
              </div>
            </div>
          </section>

          <section>
            <div className="border border-border rounded-lg p-5 bg-secondary/30 flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Pagamento seguro com Stripe</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Ao continuar, serás redirecionado para a página de pagamento segura do Stripe
                  para introduzir os dados do cartão. Cartão de teste: 4242 4242 4242 4242.
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Summary */}
        <div className="lg:col-span-1">
          <div className="border border-border rounded-xl p-6 sticky top-28">
            <h2 className="text-lg font-medium mb-4">Resumo da encomenda</h2>
            <div className="space-y-4 mb-5">
              {items.map((item) => (
                <div key={item.id} className="flex gap-3">
                  <div className="relative shrink-0">
                    <img src={item.image} alt={item.name} className="h-16 w-16 rounded-md object-cover bg-secondary" />
                    <span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-foreground text-white text-[11px] flex items-center justify-center">
                      {item.qty}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium line-clamp-2">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.brand}</p>
                  </div>
                  <span className="text-sm font-medium">€ {(item.price * item.qty).toFixed(2)}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2 py-4 border-t border-border text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Subtotal</span>
                <span>€ {subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Envio</span>
                <span>{shipping === 0 ? "Grátis" : `€ ${shipping.toFixed(2)}`}</span>
              </div>
              <p className="text-xs text-muted-foreground pt-1">Impostos calculados no pagamento.</p>
            </div>

            <div className="flex justify-between items-baseline py-4 border-t border-border">
              <span className="font-medium">Total</span>
              <span className="text-2xl font-semibold">€ {total.toFixed(2)}</span>
            </div>

            <Button type="submit" disabled={submitting} className="w-full h-12 rounded-full text-base">
              {submitting ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> A redirecionar...</>
              ) : (
                <><Lock className="mr-2 h-4 w-4" /> Pagar com segurança</>
              )}
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-3">
              Ao finalizar, aceitas os nossos Termos & Condições.
            </p>
          </div>
        </div>
      </form>
    </div>
  );
}
